import { Box, Flex, Menu, MenuButton, Portal } from '@chakra-ui/react'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { every, isEqual } from 'lodash-es'
import { memo } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useUser } from '@/client/context/user'

import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import { MenuList } from '@/components/Worlds/UI/Menu/Menu'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

import useMenuItemGroups from './useMenuItemGroups'

function SettingsButtonInternal({ itemGroups }: { itemGroups: MenuItemType[][] }): JSX.Element {
  const user = useUser()
  return (
    <Box flexShrink={0}>
      <Menu offset={[0, 16]} strategy="fixed">
        <MenuButton as={Box} cursor="pointer" className="group">
          <Flex position="relative" direction="row" alignItems="center" gap="8px" pr="12px">
            <Box
              position="absolute"
              width="100%"
              height="32px"
              borderRadius="16px"
              border={`2px solid ${chakraTokenToVar('border.muted')}`}
              transition="all 0.2s ease-in-out"
              _groupHover={{
                borderColor: 'border.emphasized',
              }}
            />
            <Avatar size="sm" user={{ ...user, avatarUrl: user.avatarUrl ?? '' }} hideOnlineBadge />
            <FontAwesomeIcon icon={faChevronDown} color={chakraTokenToVar('fg.subtle')} size="sm" />
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList itemGroups={itemGroups} />
        </Portal>
      </Menu>
    </Box>
  )
}

const SettingsButtonMemoized = memo(SettingsButtonInternal, (prev, next) => {
  // Check that the labels of all the items in each group are the same
  if (prev.itemGroups.length !== next.itemGroups.length) {
    return false
  }

  return every(prev.itemGroups, (group, index) =>
    isEqual(
      group.map(({ label }) => label),
      next.itemGroups[index].map(({ label }) => label)
    )
  )
})

export default function SettingsButton(): JSX.Element {
  const itemGroups = useMenuItemGroups()

  return <SettingsButtonMemoized itemGroups={itemGroups} />
}
